import { useEffect, useCallback, useState, useRef } from 'react'
import { ChatContainer } from './chat/ChatContainer'
import { PublisherAdContainer } from './ads/PublisherAdContainer'
import { RTLProvider } from '../contexts/RTLContext'
import { FloatingProvider, useFloating } from '../contexts/FloatingContext'
import { InteractionProvider, useInteraction } from '../contexts/InteractionContext'
import { useProjectConfig } from '../hooks'
import { widgetLogger } from '../utils/logger'
import { CloseButton } from './ui/close-button'
import type { ProjectConfig } from '@kwry/common'

interface EmbedWidgetConfig {
  projectId: string
  theme: 'light' | 'dark' | 'auto'
  width: string
  height: string
  primaryColor?: string
  position: 'inline' | 'bottom-right' | 'bottom-left'
  autoDetectContent: boolean

  language?: 'eng' | 'heb' | 'arb'
  direction?: 'ltr' | 'rtl' | 'auto'
  bottomOffset?: string
}

interface ArticleContent {
  title: string
  content: string
  author?: string
  url: string
}

interface AppContentProps {
  config: EmbedWidgetConfig
  content: ArticleContent | null
  currentChatHeight: number
  setCurrentChatHeight: (height: number) => void
  onOpenLink?: (url: string) => void
  onCenterRequest?: (height: number) => void
}

interface AppContentInnerProps extends AppContentProps {
  config: EmbedWidgetConfig
  content: ArticleContent | null
  currentChatHeight: number
  setCurrentChatHeight: (height: number) => void
  onOpenLink?: (url: string) => void
  onCenterRequest?: (height: number) => void
}

// Inner component that uses the floating context
function AppContentInner({ 
  config, 
  content, 
  currentChatHeight, 
  setCurrentChatHeight,
  onOpenLink: externalOnOpenLink,
  onCenterRequest
}: AppContentInnerProps) {
  // Get floating state from context
  const { isFloating, isMobile } = useFloating()
  
  // Get interaction state from context
  const { 
    isInputFocused, 
    hasStartedChat: hasStartedChatFromContext, 
    isChatCollapsed,
    setChatStarted,
    setChatCollapsed,
    setInputFocused
  } = useInteraction()
  
  // Track whether chat has started (first message sent)
  const [hasStartedChat, setHasStartedChat] = useState(false)
  const adContainerRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [actualChatHeight, setActualChatHeight] = useState(200) // Store the actual chat container height separately
  
  // Fetch project configuration using hook (now within ApiClientProvider)
  const { 
    data: projectConfig, 
    isLoading: isLoadingProjectConfig,
    error: projectConfigError 
  } = useProjectConfig()

  // Log project config loading state
  useEffect(() => {
    if (projectConfig) {
      widgetLogger.info('Project config loaded:', projectConfig)
    }
    if (projectConfigError) {
      widgetLogger.error('Error loading project config:', projectConfigError)
    }
  }, [projectConfig, projectConfigError])

  // Check if we should show ads below the chat container
  // Only show under-chat ads if placement is configured AND chat has started
  // AND not in mobile floating mode (would break layout)
  const shouldShowUnderChatAds = projectConfig?.adConfig?.adPlacement === 'under_chat' && 
    hasStartedChat && 
    !isFloating;

  // Calculate total height including ad container
  const calculateTotalHeight = useCallback((chatHeight: number) => {
    let totalHeight = chatHeight
    
    // Add ad container height if it should be shown
    if (shouldShowUnderChatAds && adContainerRef.current) {
      const adContainerHeight = adContainerRef.current.getBoundingClientRect().height
      const adContainerMargins = 32 // mt-4 = 16px top + 16px bottom estimated
      totalHeight += adContainerHeight + adContainerMargins
      widgetLogger.debug('Adding ad container height to total', { 
        adContainerHeight, 
        adContainerMargins, 
        totalHeight 
      })
    }
    
    return totalHeight
  }, [shouldShowUnderChatAds])

  // Handle resize and update current chat height
  const handleResize = useCallback((newChatHeight: number) => {
    if (isFloating) {
      // In mobile floating mode, skip height calculations
      // Container manages its own height via CSS constraints
      widgetLogger.debug('Mobile floating mode - height managed by CSS', { 
        requestedHeight: newChatHeight 
      })
      return
    }
    
    // Store the actual chat container height
    setActualChatHeight(newChatHeight)
    
    // Calculate total height including ads if shown
    const totalHeight = calculateTotalHeight(newChatHeight)
    setCurrentChatHeight(totalHeight)
    
    if (externalOnOpenLink) {
      // Shadow DOM mode - no postMessage needed, direct callback
      widgetLogger.debug('Height changed in Shadow DOM mode', { newChatHeight, totalHeight })
    } else {
      // Iframe mode - send postMessage to parent
      window.parent.postMessage({
        type: 'RESIZE_REQUEST',
        data: { height: totalHeight }
      }, '*')
    }
  }, [setCurrentChatHeight, externalOnOpenLink, calculateTotalHeight, isFloating])

  // Handle external link opening
  const handleOpenLink = (url: string) => {
    if (externalOnOpenLink) {
      // Use provided callback (Shadow DOM mode)
      externalOnOpenLink(url)
    } else {
      // Fallback to postMessage (iframe mode)
      window.parent.postMessage({
        type: 'OPEN_LINK',
        data: { url }
      }, '*')
    }
  }

  // Handle chat started notification
  const handleChatStarted = useCallback(() => {
    widgetLogger.debug('Chat started - enabling under-chat ads if configured')
    setHasStartedChat(true)
    setChatStarted(true) // Update the InteractionContext as well
  }, [setChatStarted])
  



  
  // Trigger resize when ad container visibility changes
  useEffect(() => {
    // Skip in mobile floating mode
    if (isFloating) return
    
    if (hasStartedChat && shouldShowUnderChatAds) {
      // Ad container just became visible, trigger resize after it renders
      const timeoutId = setTimeout(() => {
        // Use actualChatHeight (chat only) not currentChatHeight (which may include ads)
        const totalHeight = calculateTotalHeight(actualChatHeight)
        setCurrentChatHeight(totalHeight)
        
        if (externalOnOpenLink) {
          widgetLogger.debug('Ad container appeared - height updated in Shadow DOM mode', { totalHeight })
        } else {
          window.parent.postMessage({
            type: 'RESIZE_REQUEST',
            data: { height: totalHeight }
          }, '*')
          widgetLogger.debug('Ad container appeared - height updated in iframe mode', { totalHeight })
        }
      }, 100) // Small delay to ensure DOM is updated
      
      return () => clearTimeout(timeoutId)
    }
  }, [shouldShowUnderChatAds, hasStartedChat, actualChatHeight, calculateTotalHeight, externalOnOpenLink, setCurrentChatHeight, isFloating])

  return (
    <>
      {/* Backdrop with subtle tint for floating mode */}
      {isFloating && hasStartedChatFromContext && !isChatCollapsed && (
        <div 
          className="fixed inset-0 z-40 bg-black/20"
          onClick={() => setChatCollapsed(true)}
          onTouchMove={(e) => {
            // Prevent scrolling the background when backdrop is active
            e.preventDefault()
          }}
          aria-hidden="true"
        />
      )}
      
      <div 
        ref={containerRef}
        className={`text-foreground flex flex-col ${
          isFloating 
            ? `${(isInputFocused || (hasStartedChatFromContext && !isChatCollapsed)) ? 'bg-white' : ''} ${hasStartedChatFromContext && !isChatCollapsed ? 'border border-gray-200 border-b-0' : ''} rounded-t-2xl z-50 fixed bottom-0 left-0 right-0` 
            : 'bg-background'
        }`}
        style={{
          // Set fixed height when chat is active to prevent pushing input out of view
          height: isFloating && hasStartedChatFromContext && !isChatCollapsed ? '70vh' : isFloating ? 'auto' : undefined,
          // Apply max height constraint for all floating states
          maxHeight: isFloating ? '70vh' : undefined,
          // Set bottom position for floating mode
          bottom: isFloating ? (config.bottomOffset || '6vh') : undefined,
          zIndex: isFloating ? 602 : undefined,
          // Use flex overflow to ensure content doesn't push out
          overflow: isFloating ? 'hidden' : undefined,
          display: 'flex',
          flexDirection: 'column',
          // Add relative positioning for close button placement
          position: isFloating ? 'relative' : undefined
        }}
        onTouchMove={(e) => {
          // Prevent scroll bubbling to the background article on mobile
          if (isFloating) {
            e.stopPropagation()
          }
        }}
      >
        {/* Close/Collapse button - shown when there are messages */}
        {hasStartedChatFromContext && !isChatCollapsed && (
          <CloseButton
            onClose={() => {
              console.log('Close button clicked - resetting chat state')
              
              // Reset chat to minimal initial state (no suggestions)
              setChatStarted(false)
              setChatCollapsed(false)
              setInputFocused(false) // Clear focus to hide suggestions
              
              // Force immediate resize recalculation with multiple attempts
              const triggerResize = () => {
                if (containerRef.current) {
                  // Force the container to recalculate its natural height
                  const container = containerRef.current
                  
                  // Temporarily remove any fixed height to let it calculate naturally
                  const originalHeight = container.style.height
                  container.style.height = 'auto'
                  
                  // Get the natural height after state reset
                  requestAnimationFrame(() => {
                    const rect = container.getBoundingClientRect()
                    // For minimal state (no focus, no suggestions), use smaller minimum
                    const naturalHeight = Math.max(80, rect.height) // Smaller minimum for input-only state
                    
                    console.log('Calculated natural height after reset:', naturalHeight, 'isInputFocused:', false)
                    
                    // Set the new height
                    setCurrentChatHeight(naturalHeight)
                    handleResize(naturalHeight)
                    
                    // Restore container height style if needed
                    if (originalHeight && originalHeight !== 'auto') {
                      container.style.height = `${naturalHeight}px`
                    }
                  })
                }
              }
              
              // Trigger resize with delays to ensure state has propagated
              setTimeout(triggerResize, 50)
              setTimeout(triggerResize, 150)
              setTimeout(triggerResize, 300)
            }}
            className="absolute top-2 right-2 z-10"
          />
        )}
        <ChatContainer 
          className={isFloating ? "h-full" : "flex-none"} 
          config={config}
          content={content || undefined}
          onResize={handleResize}
          onOpenLink={handleOpenLink}
          onChatStarted={handleChatStarted}
          onCenterRequest={onCenterRequest}
          projectIconUrl={projectConfig?.iconUrl || null}
        />
        {shouldShowUnderChatAds && (
          <div ref={adContainerRef} className="under-chat-ads mt-4">
            <PublisherAdContainer 
              idPrefix="kwry-under-chat-ad"
              className="w-full"
            />
          </div>
        )}
      </div>
    </>
  )
}

// Wrapper component that fetches project config for FloatingProvider
function AppContentWithProjectConfig(props: AppContentProps) {
  // Get project config to determine mobile floating bar setting
  const { data: projectConfig } = useProjectConfig()
  
  return (
    <FloatingProvider 
      mobileFloatingBar={projectConfig?.mobileFloatingBar || false}
      position={props.config.position}
    >
      <RTLProvider 
        language={props.config.language} 
        direction={props.config.direction}
      >
        <InteractionProvider>
          <AppContentInner {...props} />
        </InteractionProvider>
      </RTLProvider>
    </FloatingProvider>
  )
}

// Main export that wraps everything with providers
export function AppContent(props: AppContentProps) {
  return (
    <AppContentWithProjectConfig {...props} />
  )
} 